import { Multiselect, SelectProps } from '@cloudscape-design/components';
import { memo, useEffect, useMemo, useState } from 'react';
import { convertList, filterArrayByArray } from 'src/utils/generalUtils';
import isEqual from 'lodash/isEqual';
import { Tag } from 'src/types/helios/Tags';

interface MultiSelectTagsProps {
  tags: Tag[];
  isLoading: boolean;
  readOnly?: boolean;
  error: string | null;
  selectedTagValues: SelectedTag[] | null;
  excludedItems?: SelectedTag[];
  onItemSelection: (selectedTagValues: SelectedTag[]) => void;
}
export interface SelectedTag {
  tagGroupName: string;
  tagValues: string[];
}

const MultiSelectTags = ({
  tags,
  error,
  isLoading,
  selectedTagValues,
  readOnly,
  excludedItems = [],
  onItemSelection,
}: MultiSelectTagsProps) => {
  const [selectedOptions, setSelectedOptions] = useState<SelectProps.Option[]>([]);

  useEffect(() => {
    if (selectedTagValues && selectedTagValues.length > 0) {
      const newSelectedOptions: SelectProps.Option[] = [];

      tags.forEach((tag) => {
        const tagValues =
          selectedTagValues.find((st) => st.tagGroupName === tag.tagGroupName)
            ?.tagValues || [];
        Object.entries(tag.values).forEach(([key, value]) => {
          if (tagValues.includes(key) && value.isActive) {
            newSelectedOptions.push({
              value: tag.tagGroupName + key,
              label: key,
              description: tag.tagGroupName,
            });
          }
        });
      });

      setSelectedOptions(newSelectedOptions);
    } else {
      setSelectedOptions([]);
    }
  }, [tags, selectedTagValues]);

  // Memoize the status type calculation
  const statusType = useMemo(() => {
    if (isLoading) return 'loading';
    if (error) return 'error';
    return 'finished';
  }, [isLoading, error]);

  // Memoize and organize tag options into groups
  const tagOptions = useMemo(() => {
    return tags
      .filter((tag) => tag.isActive)
      .map((tag) => {
        // Find excluded values for this tag
        const excludedValues =
          excludedItems?.find((item) => item.tagGroupName === tag.tagGroupName)
            ?.tagValues || [];

        return {
          label: tag.tagGroupName,
          options: Object.entries(tag.values)
            .filter(([key, value]) => value.isActive && !excludedValues.includes(key))
            .map(([key, value]) => ({
              value: tag.tagGroupName + key,
              label: key,
              description: tag.tagGroupName,
            })),
        };
      });
  }, [tags, excludedItems]);

  return (
    <Multiselect
      readOnly={readOnly}
      filteringType="auto"
      data-testid="tags-select"
      selectedOptions={selectedOptions}
      onChange={({ detail }) => {
        const values = detail.selectedOptions as SelectProps.Option[];
        setSelectedOptions(values);

        // Group selected options by tag group name
        const tagMap = new Map<string, string[]>();
        values.forEach((option) => {
          const value = option.value as string;
          const tagGroupName = value.substring(0, value.length - option.label!.length);
          const tagValues = tagMap.get(tagGroupName) || [];
          tagValues.push(option.label as string);
          tagMap.set(tagGroupName, tagValues);
        });

        // Convert to SelectedTag array
        const selectedTags: SelectedTag[] = Array.from(tagMap.entries()).map(
          ([tagGroupName, tagValues]) => ({
            tagGroupName,
            tagValues,
          }),
        );

        onItemSelection(selectedTags);
      }}
      options={tagOptions}
      errorText={error || undefined}
      loadingText="Loading tags"
      statusType={statusType}
      placeholder="Choose tags"
    />
  );
};

export default memo(MultiSelectTags, (prevProps, nextProps) => {
  return isEqual(prevProps, nextProps);
});
